import {ref, computed, watch} from '@vue/composition-api'
import {useToast} from 'vue-toastification/composition'
import ToastificationContent
  from '@core/components/toastification/ToastificationContent.vue'
import moment from 'moment-timezone'
import store from '@/store'
import ability from '@/libs/acl/ability'
import {debounce} from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'

export default function useList() {
  const refListTable = ref(null)

  const toast = useToast()

  store.dispatch('tenant/getAll')
  const tenantOptions = computed(() => store.getters['tenant/getSelectedItems'])
  const tenantFilter = ref(null)

  const tableColumns = [
    {label: 'pbx_master_account.name', key: 'name', sortable: true},
    {
      label: 'tenants.title',
      key: 'id_tenant',
      sortable: true,
      formatter: 'setTenantName'
    },
    {label: 'pbx_master_account.domain', key: 'domain', sortable: true},
    {label: 'pbx_master_account.api_url', key: 'api_url', sortable: true},
    {label: 'pbx_master_account.ws_url', key: 'ws_url', sortable: true},
    {label: 'pbx_master_account.ws_binding_port', key: 'ws_binding_port', sortable: true},
    {
      label: 'pbx_master_account.wss_binding_port',
      key: 'wss_binding_port',
      sortable: true
    },
    {label: 'pbx_master_account.sip_port', key: 'sip_port', sortable: true},
    {label: 'pbx_master_account.tls_sip_port', key: 'tls_sip_port', sortable: true},
    {label: 'pbx_master_account.external_sip_ip', key: 'external_sip_ip', sortable: true},
    {label: 'pbx_master_account.external_rtp_ip', key: 'external_rtp_ip', sortable: true},
    {label: 'pbx_master_account.call_recording', key: 'call_recording', sortable: true},
    {
      label: 'pbx_master_account.queue_timeout',
      key: 'queue_timeout',
      sortable: true,
      formatter: (value, key, item) => {
        return value ? `${value}s` : null
      }
    },
    {
      label: 'date.created_at',
      key: 'created_at',
      sortable: true,
      formatter: 'formatDateAssigned'
    },
    {
      label: 'date.updated_at',
      key: 'updated_at',
      sortable: true,
      formatter: 'formatDateAssigned'
    },
  ]

  if (ability.can('update', 'PbxMasterAccount') || ability.can('delete', 'PbxMasterAccount')) {
    tableColumns.splice(0, 0, {label: 'actions.name', key: 'actions'})
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })


  const fetchList = (ctx, callback) => {
    store.dispatch('pbx_master_account/getAll', {
      q: searchQuery.value,
      pageSize: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      id_tenant: tenantFilter.value,
    })
      .then(response => {
        callback(response.data)
        totalList.value = response.meta.total
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      id_tenant: tenantFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    tenantFilter.value = null;
    buildFilters();
  }

  const setTenantName = value => {
    let tenant = tenantOptions.value.find(tenant => tenant.value == value)
    return tenant?.label ?? ''
  }

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    setTenantName,

    tenantOptions,
    tenantFilter,

    refetchData,

    filtersCount,
    buildFilters,
    clearSearchFilters,
  }
}
